import React, { useState, useEffect } from 'react'
import { styled } from '@mui/material/styles'
import {
  Dialog,
  DialogTitle,
  DialogContent,
  Button,
  Box,
  Typography,
  Stack,
  Fade,
} from '@mui/material'
import TotpUtils from '../utils/totp'
import LinearProgress, { linearProgressClasses } from '@mui/material/LinearProgress'
import copy from 'copy-to-clipboard'
import toast from 'react-hot-toast'
import localization from '../utils/localization'

const BorderLinearProgress = styled(LinearProgress)(({ theme }) => ({
  height: 8,
  borderRadius: 4,
  [`&.${linearProgressClasses.colorPrimary}`]: {
    backgroundColor: 'rgba(0, 0, 0, 0.05)',
  },
  [`& .${linearProgressClasses.bar}`]: {
    borderRadius: 4,
    transition: 'background-color 0.3s ease, transform 0.4s linear',
  },
}));

let globalTotpSecret = ''

function ModalTotp(props) {
  const [totpToken, setTotpToken] = useState('')
  const [totpNumbers, setTotpNumbers] = useState([])
  const [progress, setProgress] = useState(0)

  function calcTotpToken() {
    let token = '000000'
    try {
      token = TotpUtils.generate(globalTotpSecret)
    } catch (e) { }
    
    setTotpToken(token)
    let modalTotpTime = new Date().getTime()
    const p = (modalTotpTime / 1000 % 30) * 100 / 30
    setProgress(p)
    
    const tokenStr = String(token).padStart(6, '0')
    setTotpNumbers(tokenStr.split(''))
  }

  useEffect(() => {
    globalTotpSecret = props.secret
    calcTotpToken()
    let tickerID = setInterval(calcTotpToken, 1000)
    return () => clearInterval(tickerID)
  }, [props.secret])

  const isExpiring = progress > 80;

  return (
    <Dialog
      open={props.open}
      onClose={props.onClose}
      maxWidth="xs"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: '28px',
          p: 1,
          backgroundColor: '#F8FAFC',
        },
      }}
    >
      <DialogTitle
        sx={{
          fontFamily: "'Poppins', sans-serif",
          fontWeight: 800,
          textAlign: 'center',
          pt: 4,
          pb: 1,
          color: '#1E293B',
        }}
      >
        {localization.strings.googleAuthCode}
      </DialogTitle>

      <DialogContent sx={{ px: 3, pb: 4 }}>
        <Fade in={props.open} timeout={500}>
          <Box sx={{ mt: 2 }}>
            <Box sx={{ mb: 4, px: 1 }}>
              <BorderLinearProgress 
                variant="determinate" 
                value={progress} 
                sx={{
                  '& .MuiLinearProgress-bar': {
                    backgroundColor: isExpiring ? '#EF4444' : '#1E88E5',
                  }
                }}
              />
            </Box>

            <Stack direction="row" spacing={1} justifyContent="center" sx={{ mb: 4 }}>
              {totpNumbers.map((num, index) => (
                <Box
                  key={index}
                  sx={{
                    width: { xs: 42, sm: 48 },
                    height: { xs: 58, sm: 64 },
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    backgroundColor: '#fff',
                    borderRadius: '12px',
                    border: '1px solid rgba(0,0,0,0.06)',
                    boxShadow: '0 4px 12px rgba(0,0,0,0.03)',
                    transition: 'all 0.3s ease',
                  }}
                >
                  <Typography
                    variant="h4"
                    sx={{
                      fontWeight: 800,
                      color: isExpiring ? '#EF4444' : '#1E293B',
                      fontFamily: "'JetBrains Mono', 'Roboto Mono', monospace",
                      transition: 'color 0.3s ease',
                    }}
                  >
                    {num}
                  </Typography>
                </Box>
              ))}
            </Stack>

            <Button
              fullWidth
              variant="contained"
              onClick={() => {
                copy(`${totpToken}`)
                toast.success(localization.strings.copySuccess)
              }}
              sx={{
                borderRadius: '14px',
                py: 1.8,
                fontWeight: 700,
                backgroundColor: '#1E88E5',
                textTransform: 'none',
                boxShadow: '0 8px 20px rgba(30, 136, 229, 0.2)',
                '&:hover': { 
                  backgroundColor: '#1565C0',
                  boxShadow: '0 12px 24px rgba(30, 136, 229, 0.3)',
                },
              }}
            >
              {localization.strings.clickCopy}
            </Button>
          </Box>
        </Fade>
      </DialogContent>
    </Dialog>
  )
}

export { BorderLinearProgress }
export default ModalTotp
